// МАСТЕР ЗАПУСКА — TELEGRAM (TRIGGER-WIZARD, заказ владельца 15.08.2026).
//
// Молчаливого создания из шаблона больше нет: карточка шаблона БЕЗ своего мастера
// открывает этот — по виду и поведению неотличимый от «Проверки подписки» и «Бота на
// кодовое слово», но спрашивающий ТОЛЬКО одно: чей бот и с чего начинается разговор.
// Всё остальное (сообщения, шаги, развилки) уже собрано в самом шаблоне и анкетой не
// трогается — поэтому buildWorkflow отдаёт канон шаблона как есть.
//
// Мастер один на КАНАЛ, а не на шаблон: новый телеграм-шаблон получает его сам, ничего
// заводить не надо (капкан плана). Ответы такого мастера на черновик НЕ пишутся — бот
// рождается обычным и правится схемой (см. channelWizard в core.ts).
import type { WorkflowJson } from '@scriptera/ui/scenario';
import {
  wizAccountParam,
  wizEntryWords,
  type TemplateWizardDef,
  type WizardLocale,
} from './core';
// ШАГ «ЧТО ЗАПУСКАЕТ» — ОДИН И ТОТ ЖЕ У ЦЕПОЧКИ И У БОТОВ (К18.1 п.9–10, претензия
// владельца П16 06.09.2026): строит его общий модуль, а список триггеров живёт ответом
// `entries` (`BotEntry[]`) — той же формы, что в `scenario_drafts.trigger_entries`.
import {
  botTrigChat,
  botTrigStore,
  tgLegacyEntries,
  trigBlocked,
  triggerStep,
} from './trigger-step';
import type { BotEntry } from '@scriptera/db/bot-entries';

/** Ключ шага «Что запускает» (по нему stepBlocked узнаёт, что сейчас на экране). */
const TG_START_STEP = 'start';

/** Старые черновики (галочки `entryKinds` + плоские слова) открывает АДАПТЕР. */
const store = botTrigStore('tg', tgLegacyEntries);

interface Answers {
  /** Триггеры бота списком (К18.3 этап 2 п.1): порядок — порядок списка у человека. */
  entries: BotEntry[];
}

function readAnswers(a: Record<string, unknown>): Answers {
  return { entries: store.read(a) };
}

export const triggerTg: TemplateWizardDef = {
  id: 'trigger-tg',
  // Шаблона своего нет: мастер открывает любой телеграм-шаблон без собственной анкеты.
  templateSource: '',
  channelWizard: true,
  accountChannel: 'telegram',

  // ЗНАКОМСТВО (ТЗ-2 З1). Мастер запуска один на всю сеть — имя и описание бота сюда
  // вставляет карточка каталога (wizWithLead в bot-templates); здесь запасной текст.
  // Чат на первом экране — сцена «Доводит до оплаты» с примеров работ (ТЗ-3 Т1).
  demoScene: 's3',

  lead(locale: WizardLocale) {
    const ru = locale === 'ru';
    return {
      title: ru ? 'Новый бот в Telegram' : 'New Telegram bot',
      text: ru
        ? 'Бот уже собран: сообщения и ответы готовы. Осталось указать, в каком боте он работает и с чего начинается разговор.'
        : 'The bot is already assembled: the messages and replies are ready. Specify which bot it works in and what starts the conversation.',
    };
  },

  steps(locale: WizardLocale) {
    const ru = locale === 'ru';
    return [
      // ШАГ 1 — «Бот». Подводку (что делает этот бот) в первый шаг вставляет карточка
      // каталога: там живое описание шаблона, а деф один на все шаблоны сети. Здесь —
      // запасной текст на случай шаблона без описания.
      {
        key: 'account',
        title: ru ? 'Бот' : 'Bot',
        intro: ru
          ? 'Выберите бота, в котором будет работать сценарий, и укажите, с чего начинается разговор. Сообщения уже готовы, их можно изменить после создания.'
          : 'Select the bot for this scenario and specify what starts the conversation. The messages are already prepared; you can edit them after creation.',
        params: [wizAccountParam('telegram', locale)],
      },
      // ШАГ 2 — «Что запускает». ТОТ ЖЕ ШАГ, ЧТО У ЦЕПОЧКИ (К18.1 п.9): строки триггеров
      // и «Добавить триггер», настройка каждого — своей страницей, добавление по
      // «Готово». Меню — пять видов Телеграма; «Прошёл бота» и «Получил метку» здесь не
      // бывает (это виды цепочки).
      triggerStep<BotEntry>(locale, {
        kinds: 'tg',
        key: TG_START_STEP,
        title: ru ? 'Что запускает' : 'What starts it',
        // ПОДВОДКА БЕЗ СЛОВА «ТРИГГЕР» (ТЗ USERTEST-1014 п.4, владелец 14.10.2026): тот же текст, что у «Подарка за
        // подписку»; прежний хвост tgEntryStepHint вошёл в неё и отдельно не добавляется.
        intro: ru
          ? 'Укажите, с чего начинается разговор: пользователь нажал «Начать», отправил кодовое слово или перешёл по вашей ссылке. Можно добавить несколько вариантов — если подходят сразу несколько, сработает самый точный.'
          : 'Specify what starts the conversation: the user taps “Start”, sends a code word or opens your link. You can add several options — if several match at once, the most precise one is used.',
        store,
        chat: botTrigChat,
      }),
    ];
  },

  // Превью-телефон показывает ТО, ЧТО МАСТЕР НАСТРАИВАЕТ, — как человек запустит бота.
  // Реплики бота придумывать нельзя: они живут в шаблоне и анкете не видны, а нарисованный
  // ответ был бы враньём. Одинокое сообщение бота панель рисует плашкой-подсказкой
  // (предпросмотр), поэтому пустого экрана не бывает.
  preview(answers, _stepIdx, locale) {
    const a = readAnswers(answers);
    const ru = locale === 'ru';
    // Отмечено несколько — рисуем ПЕРВЫЙ по своду вход (start, link, exact, keyword,
    // any): телефон показывает один разговор, а не все сразу.
    const first = a.entries[0]?.kind ?? 'start';
    if (first === 'link') {
      return [
        {
          from: 'bot',
          text: ru
            ? 'Запуск по переходу из вашей ссылки'
            : 'Started by opening your link',
        },
      ];
    }
    if (first === 'exact' || first === 'keyword') {
      const word = a.entries.find((e) => e.kind === first)?.keywords[0] ?? '';
      if (!word) {
        return [
          {
            from: 'bot',
            text: ru ? 'Добавьте кодовое слово, и оно появится здесь' : 'Add a code word and it will appear here',
          },
        ];
      }
      return [{ from: 'user', text: word }];
    }
    return [{ from: 'user', text: first === 'any' ? (ru ? 'Привет!' : 'Hi!') : '/start' }];
  },

  // Содержимое бота — из шаблона: анкета настраивает только запуск, канон не трогаем.
  buildWorkflow(template: WorkflowJson): WorkflowJson {
    return template;
  },

  // Условия запуска черновика — из ответов (без этого выбор в анкете не доехал бы до
  // диспетчера: черновик копирует условия карточки шаблона).
  // ВХОДЫ БОТА (ТЗ 02.09 §6): по строке на каждую отмеченную галочку — их порядок и
  // разбор общий у обеих телеграм-анкет (tgEntriesOf в core).
  triggerMeta(answers) {
    return { entries: readAnswers(answers).entries };
  },

  // ПОЛОВИНЧАТЫЙ ТРИГГЕР НЕ ПУСКАЕТ ДАЛЬШЕ (К18.3 этап 2 п.1): слова и подпись источника
  // живут на СВОЕЙ странице, обязательность поля шага их больше не ловит — иначе в базу
  // уехал бы список, который `parseBotEntries` отобьёт 400.
  stepBlocked(answers, stepKey, locale) {
    if (stepKey !== TG_START_STEP) return null;
    const list = readAnswers(answers).entries;
    if (list.length === 0) return wizEntryWords(locale).empty;
    return trigBlocked('tg', locale, list, answers);
  },
};
